import { useCallback, useMemo } from 'react';
import { Skeleton, SkeletonCircle } from '@chakra-ui/react';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { format } from 'date-fns';
import CandidateFiltersManager, {
  CandidateFiltersManagerType,
} from '@/components/CandidateFiltersManager';
import ListHeader, { ListHeaderType } from '@/components/ListHeader';
import StatusTag from '@/components/StatusTag';
import { getEndpointWithParams } from '@/routes/config';
import { Candidate } from '@/services/candidates';
import Pagination, { PaginationTypes } from '@/trio-ui/Pagination';
import Table, { Td, Th, Tr } from '@/trio-ui/Table';
import { CandidateTechStacksType } from '@/types';
import { Paths } from '@/types/routes';
import { getMainTechSkillsFormatted } from '@/utils/candidate';
import { fullDateWithHours } from '@/utils/checkins';
import * as Styles from './CandidatesList.styles';
import NewCandidateForm, { NewCandidateFormType } from '../NewCandidate';

type CandidatesListComponentProps = CandidateFiltersManagerType &
  PaginationTypes &
  NewCandidateFormType &
  ListHeaderType & {
    candidatesList: Candidate[];
    isLoading: boolean;
    isNewCandidateFormOpened: boolean;
    showFilters: boolean;
    onShowFiltersHandler: () => void;
  };

const CandidatesListComponent = function ({
  onAddNewCandidate,
  isAddingFiltersHandler,
  showFilters,
  onShowFiltersHandler,
  candidatesList,
  isLoading,
  onUpdateSortBy,
  onChangePageHandler,
  onChangeRecordsPerPage,
  count,
  itemsPerPage,
  lastPage,
  currentPage,
  onSelectFilters,
  preDefinedFilters,
  isNewCandidateFormOpened,
  onDirtyForm,
  onShowCandidateFormClick,
  ...props
}: CandidatesListComponentProps): JSX.Element {
  const renderHeader = useMemo(() => {
    return (
      <Tr>
        <Th>Candidate Name</Th>
        <Th>Developer Type</Th>
        <Th>Main Tech Stack</Th>
        <Th>Status</Th>
        <Th>Profile</Th>
        <Th>Fintech Experience</Th>
        <Th>Country</Th>
        <Th>Source</Th>
        <Th>English Level</Th>
        <Th>Candidate Owner</Th>
        <Th>Created Time</Th>
        <Th>Modified Time</Th>
      </Tr>
    );
  }, []);

  const renderEmptyStateRows = (i: number): JSX.Element => (
    <Tr key={i}>
      <Td>
        <div className="tw-flex tw-items-center">
          <SkeletonCircle width={6} height={6} className="tw-mr-3" />
          <Skeleton className="tw-h-3.5 tw-w-[100px]" />
        </div>
      </Td>
      <Td>
        <Skeleton className="tw-h-3.5 tw-w-[100px]" />
      </Td>
      <Td>
        <div className="tw-flex tw-items-center tw-gap-1.5">
          <Skeleton className="tw-h-[21px] tw-w-[56px]" />
          <Skeleton className="tw-h-[21px] tw-w-[40px]" />
        </div>
      </Td>
      <Td>
        <div className="tw-flex tw-items-center">
          <SkeletonCircle className="!tw-w-[5px] !tw-h-[5px] tw-mr-[5px]" />
          <Skeleton className="tw-h-3.5 tw-w-[60px]" />
        </div>
      </Td>
      <Td textAlign="center">
        <OpenInNewOutlinedIcon sx={{ color: '#D9D9D9', width: 12, height: 12 }} />
      </Td>
      <Td>
        <Skeleton className="tw-h-3.5 tw-w-[60px]" />
      </Td>
      <Td>
        <Skeleton className="tw-h-3.5 tw-w-[150px]" />
      </Td>
      <Td>
        <Skeleton className="tw-h-3.5 tw-w-[100px]" />
      </Td>
      <Td>
        <Skeleton className="tw-h-3.5 tw-w-[100px]" />
      </Td>
      <Td>
        <Skeleton className="tw-h-3.5 tw-w-[150px]" />
      </Td>
      <Td>
        <Skeleton className="tw-h-3.5 tw-w-[150px]" />
      </Td>
      <Td>
        <Skeleton className="tw-h-3.5 tw-w-[150px]" />
      </Td>
    </Tr>
  );

  const renderRow = useCallback((data: Candidate) => {
    const createdAt = format(new Date(data.createdAt), fullDateWithHours);
    const updatedAt = format(new Date(data.updatedAt), fullDateWithHours);
    const techStackFormatted = getMainTechSkillsFormatted(
      data?.skills as CandidateTechStacksType[]
    );

    const candidateLink = getEndpointWithParams(Paths.CANDIDATE_DETAILS, {
      id: data.id,
    });
    const englishLevelFormatted = data?.englishLevel?.toLocaleLowerCase().replace(/_/g, ' ');

    return (
      <Tr key={data.id}>
        <Td>
          <Styles.Centered>
            <Styles.CustomUserInitials
              size={24}
              pictureUrl={data?.pictureUrl ?? ''}
              firstName={data.firstName}
              lastName={data?.lastName ?? ''}
            />
            <Styles.TableLink to={candidateLink} className="capitalize">
              {data.firstName} {data.lastName}
            </Styles.TableLink>
          </Styles.Centered>
        </Td>
        <Td>{data?.developerType}</Td>

        <Td>
          {techStackFormatted.map((stack, index) => (
            <Styles.CustomTechStackBadge
              key={`${stack}-${data.id}-${index.toString()}`}
              title={stack}
            />
          ))}
        </Td>
        <Td>{data.status && <StatusTag status={data.status} />}</Td>

        <Td textAlign="center">
          {data?.trioProfileUrl ? (
            <a
              aria-label="Open candidate profile link"
              rel="noreferrer"
              href={data.trioProfileUrl}
              target="_blank"
              style={{ padding: 4.5 }}
            >
              <OpenInNewOutlinedIcon sx={{ color: '#054ad9', width: 12, height: 12 }} />
            </a>
          ) : (
            <OpenInNewOutlinedIcon sx={{ color: '#D9D9D9', width: 12, height: 12 }} />
          )}
        </Td>
        <Td>{data?.hasFintechExperience ? 'Yes' : 'No'}</Td>
        <Td>{data?.country}</Td>
        <Td>{data?.source}</Td>
        <Td className="capitalize">{englishLevelFormatted}</Td>
        <Td>{data?.candidateOwner}</Td>
        <Td>{createdAt}</Td>
        <Td>{updatedAt}</Td>
      </Tr>
    );
  }, []);

  return (
    <Styles.Content isShowingFilters={showFilters} data-testid="candidate-list">
      <ListHeader
        {...props}
        onShowCandidateFormClick={onShowCandidateFormClick}
        onUpdateSortBy={onUpdateSortBy}
        onShowFiltersHandler={onShowFiltersHandler}
      />
      {showFilters && (
        <CandidateFiltersManager
          isAddingFiltersHandler={isAddingFiltersHandler}
          preDefinedFilters={preDefinedFilters}
          onSelectFilters={onSelectFilters}
        />
      )}
      <Styles.NewCandidateForm
        data-testid="new-candidate-form"
        id="new-candidate-form"
        isOpened={isNewCandidateFormOpened}
      >
        <NewCandidateForm
          {...props}
          onAddNewCandidate={onAddNewCandidate}
          onDirtyForm={onDirtyForm}
          data-testid={`new-candidate-form-${isNewCandidateFormOpened ? 'opened' : 'closed'}`}
          isFormDisabled={!isNewCandidateFormOpened}
        />
      </Styles.NewCandidateForm>

      {!candidatesList.length && !isLoading && (
        <Styles.NotFound>
          <Styles.NotFoundSubtitle>Your search did not match any candidate</Styles.NotFoundSubtitle>
          <p>Try to set different filters.</p>
          <p>You can always use the search bar to find candidates too.</p>
        </Styles.NotFound>
      )}

      {(isLoading || !!candidatesList.length) && (
        <>
          {isLoading ? (
            <Table
              renderHeader={renderHeader}
              renderRow={renderEmptyStateRows}
              data={[...Array(itemsPerPage).keys()].map((key) => key + 1)}
            />
          ) : (
            <Table renderHeader={renderHeader} renderRow={renderRow} data={candidatesList} />
          )}
          <Pagination
            onChangeRecordsPerPage={onChangeRecordsPerPage}
            lastPage={lastPage}
            itemsPerPage={itemsPerPage}
            count={count}
            currentPage={currentPage}
            onChangePageHandler={onChangePageHandler}
          />
        </>
      )}
    </Styles.Content>
  );
};

export default CandidatesListComponent;
